import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api-client'
import { toastStrings } from '@/lib/toasts'
import type { Patient } from '@rezeta/shared'
import type { CreatePatientDto, UpdatePatientDto } from '@rezeta/shared'

interface PatientListResponse {
  items: Patient[]
  hasMore: boolean
  nextCursor?: string
}

interface PatientListParams {
  search?: string
  cursor?: string
}

const PATIENTS_KEY = 'patients'

export function usePatients(
  params?: PatientListParams,
): UseQueryResult<PatientListResponse, Error> {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.set('search', params.search)
  if (params?.cursor) searchParams.set('cursor', params.cursor)
  const qs = searchParams.toString()

  return useQuery({
    queryKey: [PATIENTS_KEY, params],
    queryFn: () => apiClient.get<PatientListResponse>(`/v1/patients${qs ? `?${qs}` : ''}`),
  })
}

export function usePatient(id: string): UseQueryResult<Patient, Error> {
  return useQuery({
    queryKey: [PATIENTS_KEY, id],
    queryFn: () => apiClient.get<Patient>(`/v1/patients/${id}`),
    enabled: Boolean(id),
  })
}

export function useCreatePatient(): UseMutationResult<Patient, Error, CreatePatientDto> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreatePatientDto) => apiClient.post<Patient>('/v1/patients', dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [PATIENTS_KEY] })
      toast.success(toastStrings.patientCreated)
    },
    onError: () => {
      toast.error(toastStrings.errorPatientCreate)
    },
  })
}

export function useUpdatePatient(id: string): UseMutationResult<Patient, Error, UpdatePatientDto> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: UpdatePatientDto) => apiClient.patch<Patient>(`/v1/patients/${id}`, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [PATIENTS_KEY] })
      toast.success(toastStrings.patientUpdated)
    },
    onError: () => {
      toast.error(toastStrings.errorPatientUpdate)
    },
  })
}

export function useDeletePatient(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/patients/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [PATIENTS_KEY] })
      toast.success(toastStrings.patientDeleted)
    },
    onError: () => {
      toast.error(toastStrings.errorPatientDelete)
    },
  })
}
